import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import HomeCard from './HomeCard';
import { Home } from '../types';

interface CarouselProps {
  homes: Home[];
  title?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({ 
  homes, 
  title, 
  autoPlay = false, 
  autoPlayInterval = 5000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(1);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerView(3);
      } else if (window.innerWidth >= 768) {
        setItemsPerView(2);
      } else {
        setItemsPerView(1);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  useEffect(() => {
    if (autoPlay && homes.length > itemsPerView) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => 
          prev + itemsPerView >= homes.length ? 0 : prev + 1
        );
      }, autoPlayInterval);

      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayInterval, homes.length, itemsPerView]);

  const goToPrevious = () => {
    setCurrentIndex(prev => 
      prev === 0 ? Math.max(0, homes.length - itemsPerView) : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex(prev => 
      prev + itemsPerView >= homes.length ? 0 : prev + 1
    );
  };

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex + itemsPerView < homes.length;

  if (homes.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {title && (
        <h3 className="text-2xl font-bold text-[#1F2937] mb-6">{title}</h3>
      )}
      
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-300 ease-in-out"
          style={{ 
            transform: `translateX(-${(currentIndex * 100) / itemsPerView}%)`,
            width: `${(homes.length * 100) / itemsPerView}%`
          }}
        >
          {homes.map((home) => (
            <div 
              key={home.id} 
              className="px-2"
              style={{ width: `${100 / homes.length}%` }}
            >
              <HomeCard home={home} />
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        {homes.length > itemsPerView && (
          <>
            <button
              onClick={goToPrevious}
              disabled={!canGoPrevious}
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
                canGoPrevious
                  ? 'bg-white text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={goToNext}
              disabled={!canGoNext}
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
                canGoNext
                  ? 'bg-white text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {homes.length > itemsPerView && (
        <div className="flex justify-center mt-6 space-x-2">
          {Array.from({ length: Math.ceil(homes.length / itemsPerView) }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index * itemsPerView)}
              className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                Math.floor(currentIndex / itemsPerView) === index
                  ? 'bg-[#FF5A1F]'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;